import { Avatar, AvatarFallback } from "@bcc/components/ui/avatar";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import { Mail, MoreHorizontal, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  setCustomersTableHeaderTestId,
  setCustomersTableRowTestId,
  setCustomersTableTestId,
  setCustomersTableTierBadgeTestId,
} from "./customers-table.utils";

export interface CustomerData {
  id: number;
  name: string;
  email: string;
  phone: string;
  orders: number;
  totalSpent: string;
  status: "active" | "inactive";
  tier: "VIP" | "Regular";
}

interface CustomersTableProps {
  data: CustomerData[];
  testId?: string;
}

const tierConfig = {
  VIP: {
    className:
      "bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-950 dark:text-purple-400",
  },
  Regular: {
    className: "bg-muted text-muted-foreground hover:bg-muted",
  },
};

// Cell Components
interface CustomerCellProps {
  name: string;
  email: string;
}

function CustomerCell({ name, email }: CustomerCellProps) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-10 w-10">
        <AvatarFallback className="bg-primary text-primary-foreground">
          {name
            .split(" ")
            .map((n) => n[0])
            .join("")}
        </AvatarFallback>
      </Avatar>
      <div>
        <p className="font-medium">{name}</p>
        <p className="text-sm text-muted-foreground">{email}</p>
      </div>
    </div>
  );
}

interface ContactCellProps {
  email: string;
  phone: string;
}

function ContactCell({ email, phone }: ContactCellProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-sm">
        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{email}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{phone}</span>
      </div>
    </div>
  );
}

interface OrdersCellProps {
  orders: number;
}

function OrdersCell({ orders }: OrdersCellProps) {
  return <span className="text-sm font-medium">{orders}</span>;
}

interface TotalSpentCellProps {
  totalSpent: string;
}

function TotalSpentCell({ totalSpent }: TotalSpentCellProps) {
  return <span className="text-sm font-medium">{totalSpent}</span>;
}

interface TierCellProps {
  tier: "VIP" | "Regular";
}

function TierCell({ tier }: TierCellProps) {
  return (
    <Badge
      variant="secondary"
      className={tierConfig[tier].className}
      data-testid={setCustomersTableTierBadgeTestId(tier)}
    >
      {tier}
    </Badge>
  );
}

function ActionsCell() {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">
            {t("views.customersTable.actions.openMenu")}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem>
          {t("views.customersTable.actions.viewProfile")}
        </DropdownMenuItem>
        <DropdownMenuItem>
          {t("views.customersTable.actions.sendEmail")}
        </DropdownMenuItem>
        <DropdownMenuItem>
          {t("views.customersTable.actions.viewOrders")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface CustomerRowProps {
  customer: CustomerData;
}

function CustomerRow({ customer }: CustomerRowProps) {
  return (
    <tr
      className="hover:bg-muted/50 transition-colors"
      data-testid={setCustomersTableRowTestId(customer.id)}
    >
      <td className="px-6 py-4">
        <CustomerCell name={customer.name} email={customer.email} />
      </td>
      <td className="px-6 py-4">
        <ContactCell email={customer.email} phone={customer.phone} />
      </td>
      <td className="px-6 py-4">
        <OrdersCell orders={customer.orders} />
      </td>
      <td className="px-6 py-4">
        <TotalSpentCell totalSpent={customer.totalSpent} />
      </td>
      <td className="px-6 py-4">
        <TierCell tier={customer.tier} />
      </td>
      <td className="px-6 py-4 text-right">
        <ActionsCell />
      </td>
    </tr>
  );
}

export function CustomersTable({ data, testId }: CustomersTableProps) {
  const { t } = useTranslation();
  return (
    <Card testId={testId ?? setCustomersTableTestId("container")}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("customer")}
                >
                  {t("views.customersTable.columns.customer")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("contact")}
                >
                  {t("views.customersTable.columns.contact")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("orders")}
                >
                  {t("views.customersTable.columns.orders")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("total-spent")}
                >
                  {t("views.customersTable.columns.totalSpent")}
                </th>
                <th
                  className="px-6 py-4 text-left text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("tier")}
                >
                  {t("views.customersTable.columns.tier")}
                </th>
                <th
                  className="px-6 py-4 text-right text-sm font-semibold"
                  data-testid={setCustomersTableHeaderTestId("actions")}
                >
                  {t("views.customersTable.columns.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((customer) => (
                <CustomerRow key={customer.id} customer={customer} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
